import useRequest from "../../Hooks/useRequest";
import { Report } from "../../Interfaces/TableInterfaces";
import { packetErrorHandler } from "../../Utils/requestUtils";
import { ErrorTableReport } from "../../Interfaces/orderInterfaces";
import { ReactNode, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { CommonTableUI } from "../../Tables/TableTemplate/common";
import Button from "../../Components/Button";
import SahraConnections from "../Sahra/SahraConnections";
import FileBulkOrderTableUI from "../../Tables/Order/FileBulkOrder/FileBulkOrderTableUI";
import Loading from "../../Components/Loading/Loading";
import FullScreenLoading from "../../Components/Loading/FullScreenLoading";

interface IProps {
  fileRequest: () => Promise<Report<any>>;
  bulkOrderRequest: (
    orderRows: Array<any> | null
  ) => Promise<Report<ErrorTableReport>>;
  onClose: () => void;
  getUserIds: (orderRows: any[] | null) => any;
  renderPreview?: (report: Report<any>, onLoadingChange: (loading: boolean) => void) => ReactNode;
  isRowSendable?: (row: any) => boolean;
}
enum ModalMode {
  BulkOrderPreview,
  BulkOrderUsers,
  BulkOrderResponseTable,
}

const FilePreviewer = ({
  bulkOrderRequest,
  fileRequest,
  onClose,
  getUserIds,
  renderPreview,
  isRowSendable,
}: IProps) => {
  const [bulkOrderRows, setBulkOrderRows] = useState<Array<any> | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const addBulkOrderRequest = useRequest(
    () => bulkOrderRequest(bulkOrderRows),
    packetErrorHandler
  );

  const fileOrderRequest = useRequest(fileRequest, errorHandler);
  const [modalMode, setModalMode] = useState(ModalMode.BulkOrderPreview);
  const [uploadFileErrorTableData, setUploadFileErrorTableData] =
    useState<Report<ErrorTableReport> | null>(null);

  useEffect(() => {
    fileOrderRequest.fetchData();
  }, [fileRequest]);

  useEffect(() => {
    if (fileOrderRequest.response) {
      setModalMode(ModalMode.BulkOrderPreview);
      setBulkOrderRows(
        fileOrderRequest.response.report.map((report) => report.orderRow).filter((row) => !isRowSendable || isRowSendable(row))
      );
      setUploadFileErrorTableData(null);
    }
  }, [fileOrderRequest.response]);

  function errorHandler(e: any) {
    if (e.response.status === 400) {
      setUploadFileErrorTableData(e.response.data);
      setModalMode(ModalMode.BulkOrderPreview);
    }

    if (e.response.status === 500) toast.error("خطا داخلی");
  }

  const getBulkOrderPreviewTable = () => {
    if (uploadFileErrorTableData)
      return (
        <div className="flex flex-col justify-between items-center h-full">
          <div className="border-2 border-white border-b-red-700 text-red-700 w-full text-center mb-3">
            خطا
          </div>
          <CommonTableUI reports={uploadFileErrorTableData} />
        </div>
      );
    if (fileOrderRequest.response)
      return (
        <div className="flex flex-col justify-between items-center h-full">
          {renderPreview ? renderPreview(fileOrderRequest.response, setPreviewLoading) : <CommonTableUI reports={fileOrderRequest.response} tableBoxClassName="compact-order-preview compact-order-preview-wide" />}
          <div className="mt-3 w-full">
            <Button
              onClick={() => setModalMode(ModalMode.BulkOrderUsers)}
              text="ارسال"
              variant="contained"
              disabled={!bulkOrderRows?.length}
              loading={previewLoading}
            />
          </div>
        </div>
      );
    else return <></>;
  };

  const getBulkOrderSahraConnectionsModalUI = () => {
    return (
      <div className="flex flex-col justify-start items-center gap-3">
        <SahraConnections
          initialSearchParam={{ userIds: getUserIds(bulkOrderRows) }}
        />
        <Button
          onClick={bulkOrderSahraConnectionsModalBtnClickHandler}
          text="ارسال"
          variant="contained"
          loading={addBulkOrderRequest.loading}
        />
      </div>
    );
  };

  const getFileBulkOrderTableUI = () => {
    if (addBulkOrderRequest.response)
      return <FileBulkOrderTableUI reports={addBulkOrderRequest.response} />;
    if (addBulkOrderRequest.loading)
      return (
        <div className="flex justify-center items-center w-full h-60">
          <Loading className="!text-sky-500" />
        </div>
      );

    return <></>;
  };

  const getUI = () => {
    switch (modalMode) {
      case ModalMode.BulkOrderPreview:
        return getBulkOrderPreviewTable();

      case ModalMode.BulkOrderUsers:
        return getBulkOrderSahraConnectionsModalUI();

      case ModalMode.BulkOrderResponseTable:
        return getFileBulkOrderTableUI();
      default:
        return <></>;
    }
  };

  const bulkOrderSahraConnectionsModalBtnClickHandler = () => {
    addBulkOrderRequest.fetchData();
    setModalMode(ModalMode.BulkOrderResponseTable);
  };

  return (
    <div className="w-full">
      {fileOrderRequest.loading || addBulkOrderRequest.loading ? (
        <FullScreenLoading />
      ) : (
        <></>
      )}
      {getUI()}
    </div>
  );
};

export default FilePreviewer;
